import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import { X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { cx } from '../utils.ts';
import { Button, IconButton } from './Button.tsx';
import './overlay.css';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  /** Icon or art shown left of the title. */
  icon?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  bodyClassName?: string;
  hideClose?: boolean;
  initialFocus?: number | MutableRefObject<HTMLElement | null>;
  /** Close on backdrop click (default true). */
  dismissible?: boolean;
  /** Align near the top (command palettes) instead of centered. */
  align?: 'center' | 'top';
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  size = 'md',
  className,
  bodyClassName,
  hideClose,
  initialFocus,
  dismissible = true,
  align = 'center',
}: DialogProps) {
  const { refs, context } = useFloating({ open, onOpenChange });
  // The second press of a double-click lands on the backdrop of the dialog its first press opened.
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown', outsidePress: dismissible && ((e) => e.detail < 2) });
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);
  const { isMounted, status } = useTransitionStatus(context, { duration: { open: 200, close: 140 } });
  const titleId = useId();
  const descId = useId();

  if (!isMounted) return null;
  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll className="g-dialog-overlay" data-status={status} data-align={align}>
        <FloatingFocusManager context={context} initialFocus={initialFocus}>
          <div
            ref={refs.setFloating}
            className={cx('g-dialog', className)}
            data-size={size}
            data-status={status}
            aria-labelledby={title ? titleId : undefined}
            aria-describedby={description ? descId : undefined}
            {...getFloatingProps()}
          >
            {(title || !hideClose) && (
              <header className="g-dialog__header" data-empty={!title || undefined}>
                {icon && <div className="g-dialog__icon">{icon}</div>}
                <div className="g-dialog__titles">
                  {title && (
                    <h2 id={titleId} className="g-dialog__title">
                      {title}
                    </h2>
                  )}
                  {description && (
                    <p id={descId} className="g-dialog__desc">
                      {description}
                    </p>
                  )}
                </div>
                {!hideClose && (
                  <IconButton label="Close" size="sm" className="g-dialog__close" onClick={() => onOpenChange(false)} tooltip={false}>
                    <X />
                  </IconButton>
                )}
              </header>
            )}
            {children !== undefined && <div className={cx('g-dialog__body', bodyClassName)}>{children}</div>}
            {footer && <footer className="g-dialog__footer">{footer}</footer>}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}

/* ---------------------------------------------------------------------------------------------
 * Promise-based confirmations: const ok = await confirm({ title, danger: true })
 * ------------------------------------------------------------------------------------------- */

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  danger?: boolean;
  icon?: ReactNode;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { open: boolean }) | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    resolver.current?.(false);
    setState({ ...options, open: true });
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const finish = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setState((s) => (s ? { ...s, open: false } : s));
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <Dialog
          open={state.open}
          onOpenChange={(o) => !o && finish(false)}
          title={state.title}
          description={state.description}
          icon={state.icon}
          size="sm"
          initialFocus={confirmRef}
          hideClose
          footer={
            <>
              <Button variant="ghost" onClick={() => finish(false)}>
                {state.cancelLabel ?? 'Cancel'}
              </Button>
              <Button ref={confirmRef} variant={state.danger ? 'danger' : 'primary'} onClick={() => finish(true)}>
                {state.confirmLabel ?? 'Confirm'}
              </Button>
            </>
          }
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return fn;
}

/* ---------------------------------------------------------------------------------------------
 * Drawer — a modal panel sliding from the side (mobile navigation, settings on small screens)
 * ------------------------------------------------------------------------------------------- */

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: 'left' | 'right';
  width?: number | string;
  children: ReactNode;
  className?: string;
  label?: string;
}

export function Drawer({ open, onOpenChange, side = 'left', width = 300, children, className, label }: DrawerProps) {
  const { refs, context } = useFloating({ open, onOpenChange });
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);
  const { isMounted, status } = useTransitionStatus(context, { duration: { open: 240, close: 180 } });
  if (!isMounted) return null;
  return (
    <FloatingPortal>
      <FloatingOverlay lockScroll className="g-dialog-overlay g-drawer-overlay" data-status={status}>
        <FloatingFocusManager context={context}>
          <div
            ref={refs.setFloating}
            className={cx('g-drawer', className)}
            data-side={side}
            data-status={status}
            aria-label={label}
            style={{ width }}
            {...getFloatingProps()}
          >
            {children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}
